import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Spin, Typography } from "antd";
import {
  postBillingVerify,
  AGENT_REG_DRAFT_STORAGE_KEY,
  AGENT_REG_FORM_SNAPSHOT_KEY,
} from "../../services/agentRegisterBillingApi";
import {
  postSubscriptionSetupComplete,
  SUBSCRIPTION_SETUP_TOKEN_KEY,
} from "../../services/subscriptionSetupApi";
import { logAgentRegBilling } from "../../utils/agentRegBillingLog";

function _dbgReturn(msg, data) {
  void msg;
  void data;
}
import SuccessRegistrationPage from "./successRegistration/SuccessRegistrationPage";
import SuccessHeader from "./successRegistration/SuccessHeader";
import SuccessStatus from "./successRegistration/SuccessStatus";
import SuccessActions from "./successRegistration/SuccessActions";

const { Paragraph } = Typography;

/**
 * Reads current URL query as a plain object (all values stringified).
 * Does not persist or display card numbers — only what Hyp/backend put in the URL.
 */
function queryParamsToRecord() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const out = {};
    params.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  } catch {
    return {};
  }
}

function resolveDraftIdFromClient(qp) {
  const a = qp.draftId ?? qp.draft_id ?? qp.DraftId;
  const fromUrl = a != null ? String(a).trim() : "";
  if (fromUrl) return { draftId: fromUrl, source: "url" };
  try {
    const fromSs = sessionStorage.getItem(AGENT_REG_DRAFT_STORAGE_KEY);
    const s = fromSs != null ? String(fromSs).trim() : "";
    if (s) return { draftId: s, source: "sessionStorage" };
  } catch {
    /* private mode */
  }
  return { draftId: "", source: "none" };
}

function resolveSubscriptionTokenFromClient(qp) {
  const candidates = [
    qp.subscriptionToken,
    qp.subscription_token,
    qp.SubscriptionToken,
  ];
  for (const v of candidates) {
    const s = v != null ? String(v).trim() : "";
    if (s) return { token: s, source: "url" };
  }
  try {
    const fromSs = sessionStorage.getItem(SUBSCRIPTION_SETUP_TOKEN_KEY);
    const s = fromSs != null ? String(fromSs).trim() : "";
    if (s) return { token: s, source: "sessionStorage" };
  } catch {
    /* private mode */
  }
  return { token: "", source: "none" };
}

const AgentRegisterBillingReturn = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const qp = queryParamsToRecord();
        logAgentRegBilling("returned_from_hyp", {
          ux: qp.ux || qp.Ux,
          hasOrder: Boolean(qp.Order),
        });

        // Subscription-upgrade flow: reuses registration billing UI/return page.
        // If we have a subscription token (from HYP return URL or sessionStorage),
        // complete activation against the subscription endpoint — not the draft flow.
        const subFromClient = resolveSubscriptionTokenFromClient(qp);
        if (subFromClient.token) {
          _dbgReturn("subscription_complete_request", {
            tokenSource: subFromClient.source,
          });
          try {
            const res = await postSubscriptionSetupComplete(subFromClient.token, qp);
            if (!cancelled) {
              if (res?.ok === false) {
                setPhase("error");
                setMessage(
                  res?.error === "token_already_used"
                    ? "הקישור כבר נוצל — התוכנית הופעלה בעבר."
                    : res?.error === "token_expired"
                      ? "הקישור פג תוקף. יש ליצור קישור חדש מול מנהל המערכת."
                      : res?.error === "hyp_verify_failed" || res?.error === "hyp_mac_invalid"
                        ? "אימות התשלום מול ספק הסליקה נכשל. נסו שוב או צרו קישור חדש."
                        : res?.message || "השלמת שדרוג המנוי נכשלה.",
                );
                _dbgReturn("subscription_complete_error", { error: res?.error });
              } else {
                try {
                  sessionStorage.removeItem(SUBSCRIPTION_SETUP_TOKEN_KEY);
                } catch {
                  /* ignore */
                }
                setPhase("success");
                setMessage("");
                _dbgReturn("subscription_complete_ok", {});
              }
            }
          } catch (err) {
            const apiErr =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              "שגיאת שרת";
            if (!cancelled) {
              setPhase("error");
              setMessage(String(apiErr));
              _dbgReturn("subscription_complete_exception", {
                err: String(apiErr).slice(0, 120),
              });
            }
          }
          return;
        }

        const { draftId, source } = resolveDraftIdFromClient(qp);

        if (!draftId) {
          if (!cancelled) {
            setPhase("error");
            setMessage(
              "חסר מזהה טיוטה (draft). לא נמצא draftId בכתובת ולא ב-sessionStorage. חזרו לעמוד הרישום, ודאו שסיימתם את השלבים הקודמים, ונסו שוב.",
            );
            logAgentRegBilling("billing_verify_skipped", { reason: "missing_draft_id" });
          }
          return;
        }

        logAgentRegBilling("billing_verify_request", { draftId, draftIdSource: source });

        try {
          await postBillingVerify(draftId, qp);
          if (!cancelled) {
            sessionStorage.removeItem(AGENT_REG_DRAFT_STORAGE_KEY);
            try {
              sessionStorage.removeItem(AGENT_REG_FORM_SNAPSHOT_KEY);
            } catch {
              /* ignore */
            }
            setPhase("success");
            setMessage("");
            logAgentRegBilling("billing_verify_success", { draftId });
          }
        } catch (err) {
          const apiErr =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "שגיאת שרת";
          if (!cancelled) {
            setPhase("error");
            setMessage(String(apiErr));
            logAgentRegBilling("billing_verify_fail", {
              draftId,
              err: String(apiErr).slice(0, 120),
            });
          }
        }
      } catch (e) {
        if (!cancelled) {
          setPhase("error");
          setMessage("אירעה שגיאה בלתי צפויה. נסו שוב או חזרו לרישום.");
          logAgentRegBilling("billing_return_page_error", { err: String(e).slice(0, 120) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const goLogin = useCallback(() => navigate("/login"), [navigate]);
  const goDashboard = useCallback(() => navigate("/p/dashboard/home"), [navigate]);
  const goSearchOffer = useCallback(() => navigate("/p/dashboard/searchoffer"), [navigate]);

  return (
    <SuccessRegistrationPage>
      {phase === "loading" && (
        <div className="success-reg-page__loading">
          <Spin size="large" />
          <Paragraph className="success-reg-page__loading-text">מאמתים את התשלום מול השרת…</Paragraph>
        </div>
      )}

      {phase === "success" && (
        <>
          <SuccessHeader />
          <SuccessStatus />
          <SuccessActions
            onLogin={goLogin}
            onDashboard={goDashboard}
            onStartSelling={goSearchOffer}
          />
        </>
      )}

      {phase === "error" && (
        <div className="success-reg-page__error">
          <Alert
            type="error"
            showIcon
            message="לא הצלחנו להשלים את שמירת אמצעי התשלום"
            description={
              <>
                <div style={{ marginBlockEnd: 8 }}>{message}</div>
                <div>
                  טיוטת הרישום נשמרה בשרת (אם נוצרה). בעת חזרה לרישום ישוחזרו הפרטים שהזנתם (למעט
                  סיסמה — יש להזין סיסמה מחדש), כדי שלא תאלצו למלא הכל מחדש.
                </div>
              </>
            }
          />
          <Button type="primary" size="large" block onClick={() => navigate("/registeragent")}>
            נסה שוב
          </Button>
        </div>
      )}
    </SuccessRegistrationPage>
  );
};

export default AgentRegisterBillingReturn;
